import { inject, Injectable } from '@angular/core'
import * as Sentry from '@sentry/angular'
import { filter, first } from 'rxjs'
import { UsersService } from './users.service'
import { LoggerService } from './logger.service'
import { MeUser } from './users.service'
import { environment } from '../environments/environment'

const FILTERED_HOSTS = ['storage.googleapis.com']

function sameHost(url: string) {
  try {
    return FILTERED_HOSTS.includes(new URL(url).hostname)
  } catch {
    return false
  }
}

function redactUrl(url: string) {
  try {
    const parsed = new URL(url)
    const parts = parsed.pathname.split('/').filter(Boolean)
    let path = '/'
    if (parts[0]) {
      path += parts[0]
    }
    if (parts[1]) {
      path += '/' + parts[1]
    }
    if (parts[2]) {
      const dot = parts[2].lastIndexOf('.')
      const ext = dot >= 0 ? parts[2].slice(dot) : ''
      path += '/*****' + ext
    }
    return parsed.origin + path
  } catch {
    return url
  }
}

function redactUrlsInText(text: string) {
  if (typeof text !== 'string') {
    return text
  }
  return text.replace(/https?:\/\/[^\s"')]+/g, (match) => (sameHost(match) ? redactUrl(match) : match))
}

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  usersService = inject(UsersService)
  loggerService = inject(LoggerService)

  constructor() {
    this.usersService.currentUser$
      .pipe(
        filter((user): user is MeUser => user !== null),
        first()
      )
      .subscribe((user) => this.initializeSentry(user))
  }

  private initializeSentry(user: MeUser) {
    if (environment.disableSentry) {
      return
    }
    Sentry.init({
      dsn: 'https://7f307e453ac734768ca5bda335e6880b@sentry.wiseworks.ai/2',
      release: 'wiseworks-web@1.0.0',
      environment: user.environment,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          mutationLimit: 30000,
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
          mask: [
            'app-outline',
            'app-editor',
            'app-transcript',
            '.search-container .right .match .content',
            'app-previous-meeting-card .thumbnail',
            '#tinymce',
            '.tox-revisionhistory',
          ],
          block: ['video', 'app-meeting video', '.video-player', '.intercom-app'],
          networkDetailAllowUrls: [window.location.origin],
          networkCaptureBodies: true,
          networkRequestHeaders: ['User-Agent'],
          networkResponseHeaders: ['Content-Type'],
          beforeAddRecordingEvent(event: any) {
            const payload = event && event.data && event.data.payload
            if (payload === null || payload === undefined) {
              return event
            }

            if (event.data.tag === 'performanceSpan') {
              if (payload.description) {
                payload.description = redactUrlsInText(payload.description)
              }
              if (payload.data && payload.data.url && sameHost(payload.data.url)) {
                payload.data.url = redactUrl(payload.data.url)
              }
              if (payload.name) {
                payload.name = redactUrlsInText(payload.name)
              }
            }

            if (event.data.tag === 'breadcrumb') {
              if (payload && payload.message) {
                payload.message = redactUrlsInText(payload.message)
              }
              if (payload && payload.data && payload.data.url && sameHost(payload.data.url)) {
                payload.data.url = redactUrl(payload.data.url)
              }
            }

            return event
          },
        }),
      ],
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 1.0,
      replaysOnErrorSampleRate: 1.0,
      enableLogs: true,
      sendDefaultPii: true,
    })

    Sentry.addEventProcessor((event: any) => {
      if (event && event.type === 'replay_event' && Array.isArray(event.urls)) {
        event.urls = event.urls.map((url: string) => (sameHost(url) ? redactUrl(url) : url))
      }
      return event
    })

    Sentry.setUser({
      id: String(user.id),
      email: user.email,
      organization: user.organization.name,
      organizationId: String(user.organizationId),
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
    })

    this.loggerService.info(`Sentry initialized with environment ${user.environment} and user ${user.email} (${user.id}).`)
  }
}
